import React, { useEffect } from 'react';
import { Container, Row, Col, Card, Button } from 'react-bootstrap';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import {
    faShieldAlt,
    faChartLine,
    faHandshake,
    faBalanceScale,
    faCogs,
    faTasks,
    faFileInvoiceDollar
} from '@fortawesome/free-solid-svg-icons';
import AOS from 'aos';
import 'aos/dist/aos.css';

const services = [
    { icon: faFileInvoiceDollar, title: 'CT Registration', description: 'Assistance with Corporate Tax registration and obtaining Tax Registration Number (TRN).' },
    { icon: faBalanceScale, title: 'CT Advisory', description: 'Expert advice on the application of Corporate Tax law to your specific business operations.', color: 'text-success' },
    { icon: faHandshake, title: 'CT Compliance', description: 'Ensuring your company is fully compliant with all Corporate Tax regulations.', color: 'text-warning' },
    { icon: faTasks, title: 'CT Return Filing', description: 'Preparation and submission of Corporate Tax returns to the Federal Tax Authority (FTA).', color: 'text-info' },
    { icon: faShieldAlt, title: 'CT Planning', description: 'Strategic tax planning to optimize your Corporate Tax liabilities.', color: 'text-danger' },
    { icon: faChartLine, title: 'CT Impact Assessment', description: 'Analyzing the potential impact of Corporate Tax on your business model and financial results.' },
];

const reasons = [
    { title: 'In-depth Knowledge', description: 'Our experts have a comprehensive understanding of the new UAE Corporate Tax Law.' },
    { title: 'Proactive Approach', description: 'We help you prepare for and adapt to the new tax environment before it takes effect.' },
    { title: 'Tailored Solutions', description: 'Our services are customized to your company’s size, industry, and unique corporate structure.' },
    { title: 'Compliance Assurance', description: 'We ensure your business remains fully compliant, minimizing the risk of penalties.' },
    { title: 'Strategic Advisory', description: 'We provide strategic advice to help you manage your tax obligations effectively.' },
    { title: 'Dedicated Support', description: 'A dedicated team is available to answer your questions and guide you through the process.' },
];

const CorporateTax = () => {
    useEffect(() => {
        AOS.init({ duration: 1000, once: true });
    }, []);

    const sectionStyles = {
        padding: '80px 0',
        fontFamily: 'Segoe UI, sans-serif'
    };

    const leadStyle = {
        color: '#6c757d',
        fontSize: '1.25rem'
    };

    const cardBaseStyle = {
        borderRadius: '15px',
        transition: 'all 0.3s ease',
        height: '100%',
        cursor: 'pointer'
    };

    const handleMouseEnter = (e) => {
        e.currentTarget.style.backgroundColor = '#7f8986ff';
        e.currentTarget.style.color = '#fff';
        e.currentTarget.style.transform = 'translateY(-8px)';
        e.currentTarget.style.boxShadow = '0 8px 20px rgba(0,0,0,0.2)';
    };

    const handleMouseLeave = (e) => {
        e.currentTarget.style.backgroundColor = '#fff';
        e.currentTarget.style.color = '#000';
        e.currentTarget.style.transform = 'translateY(0)';
        e.currentTarget.style.boxShadow = 'none';
    };

    return (
        <>
            {/* Hero Section */}
            <section
                style={{
                    position: "relative",
                    height: "90vh",
                    backgroundImage: `url('./em3.png')`,
                    backgroundSize: "cover",
                    backgroundPosition: "center",
                    display: "flex",
                    alignItems: "center",
                    justifyContent: "center",
                    color: "#fff",
                    textAlign: "center"
                }}
            >
                <div style={{ background: "rgba(0,0,0,0.4)", padding: "20px", borderRadius: "10px" }}>
                    <p style={{ fontSize: "1.2rem" }}>Preparing your business for the new tax era</p>
                    <h1 style={{ fontWeight: "bold", fontSize: "2.5rem" }}>Seamless Corporate Tax compliance and strategic planning</h1>
                    
                    {/* WhatsApp Button */}
                    <a
                        href="https://wa.me/+971588124755"
                        target="_blank"
                        rel="noopener noreferrer"
                        className="whatsapp-btn"
                    >
                        <i className="bi bi-whatsapp me-2"></i> Chat on WhatsApp
                    </a>
                </div>
            </section>

            <main>
                {/* Intro Section */}
                <section style={{ ...sectionStyles, backgroundColor: '#fff' }}>
                    <Container>
                        <p style={leadStyle} data-aos="fade-up">
                            At <strong>Emkan</strong>, we recognize that the introduction of Corporate Tax in the UAE marks a significant shift for businesses. Our <b>Corporate Tax Services</b> are designed to guide you through this transition, ensuring you understand your obligations and are fully prepared for compliance from day one.
                            <details>
                                <summary>Read More</summary>
                                <p>
                                 
Beyond compliance, our corporate tax consulting services are designed to provide you with more. We offer professional advice, customized plans, and workable solutions that assist your business satisfy its legal requirements while increasing its financial effectiveness. Our services are tailored to meet your unique needs, whether you are a startup firm in the United Arab Emirates, a developing SME, or an established organization with broader activities.

The Emkan team contributes extensive expertise in international standards, industry practices, and UAE corporate tax law.  We examine your company's operations, financial records, and transactions to find areas where you can save money on taxes and take advantage of legal exclusions and reliefs.  In addition to preventing overpayment and safeguarding your resources, this guarantees that you maintain compliance.

 We support proactive tax preparation.  We assist you in planning forward rather than just resolving problems when they come up.  Our guidance enables you to make well-informed decisions with a clear understanding of the tax consequences, whether you are restructuring, entering new markets, or signing new contracts.

Since every company is different, we steer clear of universally applicable solutions. After taking the time to comprehend your objectives and difficulties, we develop plans that promote expansion, financial success, and long-term stability.

By selecting Emkan, you have a reliable partner who manages the intricate corporate tax system so you can concentrate on running your company. With our help, compliance is more than just fulfilling regulations; it's a chance to improve your financial situation and expand with assurance.
                                </p>
                            </details>
                        </p>
                    </Container>
                </section>

                {/* Services Section */}
                <section style={{ ...sectionStyles, backgroundColor: '#7d94a6ff' }}>
                    <Container>
                        <h2 className="text-white fw-bold text-center mb-5" data-aos="fade-up">Our Corporate Tax Services</h2>
                        <Row className="g-4">
                            {services.map((service, index) => (
                                <Col md={4} key={index} data-aos="zoom-in" data-aos-delay={index * 100}>
                                    <Card
                                        style={cardBaseStyle}
                                        className="p-4 text-center"
                                        onMouseEnter={handleMouseEnter}
                                        onMouseLeave={handleMouseLeave}
                                    >
                                        <Card.Body>
                                            <FontAwesomeIcon
                                                icon={service.icon}
                                                className={`fs-1 mb-3 ${service.color || 'text-primary'}`}
                                            />
                                            <h5 className="fw-bold">{service.title}</h5>
                                            <p>{service.description}</p>
                                        </Card.Body>
                                    </Card>
                                </Col>
                            ))}
                        </Row>
                    </Container>
                </section>

                {/* Why Choose Us Section */}
                <section style={{ ...sectionStyles, backgroundColor: '#7d94a6ff' }}>
                    <Container>
                        <h2 className="text-white fw-bold text-center mb-5" data-aos="fade-up">Why Choose Emkan for Corporate Tax?</h2>
                        <Row className="g-4">
                            {reasons.map((reason, index) => (
                                <Col md={4} key={index} data-aos="fade-up" data-aos-delay={index * 100}>
                                    <Card
                                        style={cardBaseStyle}
                                        className="p-4"
                                        onMouseEnter={handleMouseEnter}
                                        onMouseLeave={handleMouseLeave}
                                    >
                                        <Card.Body>
                                            <h5 className="fw-bold">{reason.title}</h5>
                                            <p>{reason.description}</p>
                                        </Card.Body>
                                    </Card>
                                </Col>
                            ))}
                        </Row>
                    </Container>
                </section>
            </main>
        </>
    );
};

export default CorporateTax;